import React, { useEffect, useRef, useState } from "react";
import { FaEdit, FaTimesCircle, FaTrashAlt } from "react-icons/fa";
import PopUp from "../../components/PopUp";
import {
    Box,
    FormControl,
    FormHelperText,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    Slide,
    Tab,
    Tabs,
    TextField,
} from "@mui/material";

import { GridActionsCellItem } from "@mui/x-data-grid";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { selectDevice, selectSensor, selectUserData } from "../../features/store";
import { RectBtn } from "../../components/General";
import { addSensor, deleteSensor, updateSensorData } from "../../features/slices/sensorSlice";
import { BsArrowLeftCircle } from "react-icons/bs";
import { AMMONIA, HAND_TOWEL, PAPER_ROLL, SOAP } from "../../configs/global";
import { getDeviceType } from "../../models/Device";
import { createDevice, deleteDevice as deleteDeviceApi } from "../../apis/Device.Api";
import { addDevice, deleteDevice } from "../../features/slices/deviceSlice";
import { useSnackbar } from "notistack";
import { createSensor, deleteSensor as deleteSensorApi, updateSensor } from "../../apis/Sensor.Api";

const InspectConfig = ({ openInspect, setOpenInspect, selectedConfig }) => {
    const { enqueueSnackbar } = useSnackbar();
    const sensors = useSelector(selectSensor);
    const devices = useSelector(selectDevice);
    const userData = useSelector(selectUserData);
    const [tabId, setTabId] = useState("sensor");
    const [displaySensors, setDisplaySensors] = useState([]);
    const [displayDevices, setDisplayDevices] = useState([]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [anchorUpdate, setAnchorUpdate] = useState(null);
    const open = Boolean(anchorEl);
    const openUpdate = Boolean(anchorUpdate);
    const [selectedSensor, setSelectedSensor] = useState(null);
    const [selectedDevice, setSelectedDevice] = useState(null);
    // Update Sensor
    const [updateSensorName, setUpdateSensorName] = useState("");
    const [validUpdateSensorName, setValidUpdateSensorName] = useState("");
    const [updateSensorType, setUpdateSensorType] = useState("");
    const containerRef = useRef(null);
    // Create Sensor
    const [openCreateSensor, setOpenCreateSensor] = useState(false);
    const [newSensorType, setNewSensorType] = useState("");
    const [newSensorId, setNewSensorId] = useState("");
    const [newSensorName, setNewSensorName] = useState("");
    const [typeInvalid, setTypeInvalid] = useState("");
    const [idInvalid, setIdInvalid] = useState("");
    const [nameInvalid, setNameInvalid] = useState("");
    // Create Device
    const [newDeviceType, setNewDeviceType] = useState("");
    const [newDeviceId, setNewDeviceId] = useState("");
    const [deviceTypeValid, setDeviceTypeValid] = useState("");
    const [deviceIdValid, setDeviceIdValid] = useState("");
    const [lackDeviceMsg, setLackDeviceMsg] = useState("");
    function resetStates() {
        setAnchorEl(null);
        setAnchorUpdate(null);
        setSelectedSensor(null);
        setSelectedDevice(null);
        setUpdateSensorName("");
        setUpdateSensorType("");
        setValidUpdateSensorName("");
        resetAddSensor();
    }

    function resetAddSensor() {
        setOpenCreateSensor(false);
        setNewSensorId("");
        setNewSensorType("");
        setNewSensorName("");
        setTypeInvalid("");
        setIdInvalid("");
        setNameInvalid("");
        setNewDeviceId("");
        setNewDeviceType("");
        setDeviceIdValid("");
        setDeviceTypeValid("");
    }

    const dispatch = useDispatch();
    const handleClose = () => {
        setAnchorEl(null);
        setAnchorUpdate(null);
    };

    async function validateForm() {
        let validate = true;
        let idValid = true;
        let nameValid = true;
        if (newSensorId === "") {
            setIdInvalid("Please enter id");
            validate = false;
            idValid = false;
        } else {
            setIdInvalid("");
        }
        if (newSensorName === "") {
            setNameInvalid("Please enter name");
            validate = false;
            nameValid = false;
        } else {
            setNameInvalid("");
        }
        if (newSensorType === "") {
            setTypeInvalid("Please select sensor type");
            validate = false;
        } else {
            setTypeInvalid("");
        }
        for (let sid of Object.keys(sensors)) {
            if (idValid && sid === newSensorId) {
                setIdInvalid("ID already exist");
                validate = false;
                idValid = false;
            }
            if (nameValid && sensors[sid].sensorName === newSensorName) {
                setNameInvalid("Name already exist");
                validate = false;
                nameValid = false;
            }
        }
        if (idValid) {
            setIdInvalid("");
        }
        if (nameValid) {
            setNameInvalid("");
        }

        if (validate) {
            // Add Sensor
            let r = await createSensor({
                sensor_id: newSensorId,
                sensor_name: newSensorName,
                room_id: selectedConfig.id,
                sensor_type: newSensorType,
            });
            if (r.data.networkError) {
                enqueueSnackbar(r.data.message, { variant: "error" });
                return;
            }
            if (r.data.indicator) {
                enqueueSnackbar("Sensor created.", { variant: "success" });
            } else {
                enqueueSnackbar("Failed to create sensor. This is possibly due to duplicate sensor ID or name", {
                    variant: "error",
                });
                return;
            }
            dispatch(
                addSensor({
                    sensorId: newSensorId,
                    sensorName: newSensorName,
                    roomId: selectedConfig.id,
                    sensorType: newSensorType,
                })
            );
            resetAddSensor();
        }
    }
    async function confirmAddDevice() {
        setDeviceIdValid("");
        setDeviceTypeValid("");
        if (newDeviceType === "") {
            setDeviceTypeValid("Please select a device type");
            return;
        }
        if (newDeviceId === "") {
            setDeviceIdValid("Please enter device ID");
            return;
        }
        let newDid = newDeviceType + newDeviceId;
        if (devices[newDid]) {
            setDeviceIdValid("Device ID must be unique");
            return;
        }
        // Add Device
        let r = await createDevice({
            device_id: newDid,
            room_id: selectedConfig.id,
        });
        if (r.networkError) {
            enqueueSnackbar(r.data.message, { variant: "error" });
            return;
        }
        if (r.data.indicator) {
            enqueueSnackbar("Device created.", { variant: "success" });
        } else {
            enqueueSnackbar("Failed to create device. This is possibly due to duplicate device ID", {
                variant: "error",
            });
            return;
        }
        dispatch(
            addDevice({
                deviceId: newDid,
                roomId: selectedConfig.id,
            })
        );
        resetAddSensor();
    }

    function validateUpdateSensor() {
        let valid = true;
        if (updateSensorName === "") {
            setValidUpdateSensorName("Name cannot be empty");
            valid = false;
        } else {
            for (let sid of Object.keys(sensors)) {
                if (
                    sensors[sid].sensorName === updateSensorName &&
                    sensors[sid].sensorName !== selectedSensor.sensorName
                ) {
                    setValidUpdateSensorName("Name already exist");
                    valid = false;
                }
            }
        }
        if (valid) {
            setValidUpdateSensorName("");
        }
        return valid;
    }
    async function confirmUpdateSensor() {
        if (!validateUpdateSensor()) return;
        let r = await updateSensor({
            sensor_id: selectedSensor.sensorId,
            sensor_name: updateSensorName,
            sensor_type: updateSensorType,
        });
        if (r.data.networkError) {
            enqueueSnackbar(r.data.message, { variant: "error" });
            return;
        }
        if (r.data.indicator) {
            enqueueSnackbar("Sensor updated.", { variant: "success" });
        } else {
            enqueueSnackbar("Failed to update sensor. This is possibly due to duplicate sensor name", {
                variant: "error",
            });
            return;
        }
        dispatch(
            updateSensorData({
                sensorId: selectedSensor.sensorId,
                sensorName: updateSensorName,
                sensorType: updateSensorType,
            })
        );
        setAnchorUpdate(null);
        setUpdateSensorName("");
        setUpdateSensorType("");
    }
    async function confirmDeleteSensor() {
        let r = await deleteSensorApi({
            sensor_id: selectedSensor.sensorId,
        });
        if (r.data.indicator) {
            enqueueSnackbar("Sensor deleted.", { variant: "success" });
        } else {
            enqueueSnackbar(r.data.message, { variant: "error" });
            return;
        }
        dispatch(deleteSensor({ sensorId: selectedSensor.sensorId }));
        setAnchorEl(null);
    }
    async function confirmDeleteDevice() {
        let r = await deleteDeviceApi({
            device_id: selectedDevice.deviceId,
        });

        if (r.data.indicator) {
            enqueueSnackbar("Device deleted.", { variant: "success" });
            dispatch(deleteDevice({ deviceId: selectedDevice.deviceId }));
        } else {
            enqueueSnackbar(r.data.message, { variant: "error" });
            return;
        }

        setAnchorEl(null);
    }
    useEffect(() => {
        let toDisplay = [];
        for (let sid of Object.keys(sensors)) {
            let sensor = sensors[sid];
            if (sensor.roomId !== selectedConfig.id) continue;
            let obj = {
                id: sid,
                sensorId: sid,
                sensorType: sensor.sensorType,
                sensorName: sensor.sensorName,
            };
            toDisplay.push(obj);
        }
        setDisplaySensors(toDisplay);

        let deviceDisplay = [];
        let typeDict = {
            p: 0,
            f: 0,
        };
        for (let did in devices) {
            let device = devices[did];
            if (device.roomId !== selectedConfig.id) continue;

            let obj = {
                id: did,
                deviceId: did,
                deviceType: getDeviceType(did),
            };
            deviceDisplay.push(obj);
            if (did.toUpperCase().startsWith("P")) {
                typeDict.p += 1;
            } else if (did.toUpperCase().startsWith("F")) {
                typeDict.f += 1;
            }
        }
        if (typeDict.p < 1 || typeDict.f < 1) {
            setLackDeviceMsg("Missing important devices. Please configure accordingly.");
        } else {
            setLackDeviceMsg("");
        }
        setDisplayDevices(deviceDisplay);
    }, [devices, sensors, selectedConfig]);

    const columns = [
        { field: "sensorId", headerName: "Sensor ID", flex: 2, minWidth: 100 },
        { field: "sensorType", headerName: "Type", flex: 2, minWidth: 80 },
        {
            field: "sensorName",
            headerName: "Name",
            flex: 2,
            minWidth: 100,
        },

        {
            field: "actions",
            type: "actions",
            flex: 1,
            minWidth: 10,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<FaEdit className="inpect-config-edit-icon" />}
                    label="Edit"
                    onClick={(event) => {
                        setSelectedSensor(params.row);
                        setUpdateSensorName(params.row.sensorName);
                        setUpdateSensorType(params.row.sensorType);
                        setAnchorUpdate(event.currentTarget);
                        setValidUpdateSensorName("");
                    }}
                    sx={{ marginRight: 1 }}
                />,
                <GridActionsCellItem
                    icon={<FaTrashAlt color="#e5383b" />}
                    label="Delete"
                    sx={{ color: "#e5383b" }}
                    onClick={(event) => {
                        setSelectedSensor(params.row);
                        setAnchorEl(event.currentTarget);
                    }}
                />,
            ],
        },
    ];
    const deviceColumns = [
        { field: "deviceId", headerName: "Device ID", flex: 2, minWidth: 100 },
        { field: "deviceType", headerName: "Type", flex: 2, minWidth: 80 },
        {
            field: "actions",
            type: "actions",
            flex: 1,
            minWidth: 10,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<FaTrashAlt color="#e5383b" />}
                    label="Delete"
                    sx={{ color: "#e5383b" }}
                    onClick={(event) => {
                        setSelectedDevice(params.row);
                        setAnchorEl(event.currentTarget);
                    }}
                />,
            ],
        },
    ];

    return (
        <PopUp width={80} height={85} switchOn={openInspect}>
            <div className="pop-up-content-wrapper">
                <div className="pop-up-row">
                    <h4>{selectedConfig.room}</h4>
                    <FaTimesCircle
                        className="pop-up-close-btn"
                        onClick={() => {
                            resetStates();

                            setOpenInspect(false);
                        }}
                    />
                </div>
                <div className="pop-up-division-line"></div>
                <div className="pop-up-body">
                    <div className="inspect-content-wrapper" ref={containerRef}>
                        <Slide
                            direction="right"
                            in={!openCreateSensor}
                            container={containerRef.current}
                            mountOnEnter
                            unmountOnExit
                        >
                            <div className="slider-wrapper">
                                <div className="table-with-tab-wrapper">
                                    <Tabs
                                        value={tabId}
                                        onChange={(event, newValue) => {
                                            setTabId(newValue);
                                        }}
                                        textColor="secondary"
                                        indicatorColor="secondary"
                                        TabIndicatorProps={{ style: { height: 3 } }}
                                    >
                                        <Tab label={<div className="tab-label">Sensor</div>} value={"sensor"} />
                                        <Tab label={<div className="tab-label">Device</div>} value={"device"} />
                                    </Tabs>
                                    <Box sx={{ height: "45vh", width: "100%" }}>
                                        <DataGrid
                                            rows={tabId === "sensor" ? displaySensors : displayDevices}
                                            columns={tabId === "sensor" ? columns : deviceColumns}
                                            initialState={{
                                                pagination: {
                                                    paginationModel: { page: 0, pageSize: 10 },
                                                },
                                            }}
                                            pageSizeOptions={[10, 20]}
                                            sx={{
                                                [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                                                    outline: "none",
                                                },
                                                [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                                                    {
                                                        outline: "none",
                                                    },
                                            }}
                                            disableRowSelectionOnClick
                                        />
                                    </Box>
                                </div>
                                <p className="lack-device-warning">{tabId === "device" && lackDeviceMsg}</p>
                                <div className="add-sensor-btn-wrapper">
                                    <RectBtn
                                        onClick={() => {
                                            setOpenCreateSensor(true);
                                        }}
                                    >
                                        {tabId === "sensor" ? "Add Sensor" : "Add Device"}
                                    </RectBtn>
                                </div>
                            </div>
                        </Slide>
                        <Slide
                            direction="left"
                            in={openCreateSensor}
                            container={containerRef.current}
                            mountOnEnter
                            unmountOnExit
                        >
                            <div className="slider-wrapper">
                                <div className="add-sensor">
                                    <div className="add-sensor-top">
                                        <BsArrowLeftCircle
                                            onClick={() => {
                                                resetAddSensor();
                                            }}
                                            size={"1.25rem"}
                                            className="back-icon"
                                        />
                                        <h4 className="sensor-name">
                                            {tabId === "sensor" ? "Add Sensor" : "Add Device"}
                                        </h4>
                                    </div>
                                    {tabId === "sensor" ? (
                                        <div className="forms">
                                            <TextField
                                                required
                                                id="standard-required"
                                                label="Sensor ID"
                                                defaultValue=""
                                                variant="standard"
                                                onChange={(e) => {
                                                    setNewSensorId(e.target.value);
                                                }}
                                                error={idInvalid !== ""}
                                                helperText={idInvalid}
                                            />
                                            <TextField
                                                required
                                                id="standard-required"
                                                label="Sensor Name"
                                                defaultValue=""
                                                variant="standard"
                                                onChange={(e) => {
                                                    setNewSensorName(e.target.value);
                                                }}
                                                error={nameInvalid !== ""}
                                                helperText={nameInvalid}
                                            />

                                            <FormControl
                                                variant="standard"
                                                sx={{ minWidth: 120 }}
                                                error={typeInvalid !== ""}
                                            >
                                                <InputLabel id="select-type">Sensor Type *</InputLabel>
                                                <Select
                                                    labelId="select-type"
                                                    id="select-type"
                                                    value={newSensorType}
                                                    onChange={(e) => {
                                                        setNewSensorType(e.target.value);
                                                    }}
                                                    label="Sensor Type *"
                                                    required
                                                    sx={{ minWidth: 150 }}
                                                >
                                                    <MenuItem value="">
                                                        <em>None</em>
                                                    </MenuItem>
                                                    <MenuItem value={AMMONIA}>Ammonia</MenuItem>
                                                    <MenuItem value={PAPER_ROLL}>Paper Roll</MenuItem>
                                                    <MenuItem value={HAND_TOWEL}>Hand Towel</MenuItem>
                                                    <MenuItem value={SOAP}>Soap</MenuItem>
                                                </Select>

                                                <FormHelperText>{typeInvalid}</FormHelperText>
                                            </FormControl>
                                        </div>
                                    ) : (
                                        <div className="forms">
                                            <FormControl
                                                variant="standard"
                                                sx={{ minWidth: 120 }}
                                                error={deviceTypeValid !== ""}
                                            >
                                                <InputLabel id="select-type">Device Type *</InputLabel>
                                                <Select
                                                    labelId="select-type"
                                                    id="select-type"
                                                    value={newDeviceType}
                                                    onChange={(e) => {
                                                        setNewDeviceType(e.target.value);
                                                    }}
                                                    label="Device Type *"
                                                    required
                                                    sx={{ minWidth: 150 }}
                                                >
                                                    <MenuItem value="">
                                                        <em>None</em>
                                                    </MenuItem>
                                                    <MenuItem value={"P"}>Footfall</MenuItem>
                                                    <MenuItem value={"F"}>Feedback</MenuItem>
                                                </Select>
                                                <FormHelperText>{deviceTypeValid}</FormHelperText>
                                            </FormControl>
                                            <TextField
                                                id="standard"
                                                label="Device ID"
                                                variant="standard"
                                                onChange={(e) => {
                                                    if (e.target.value.length < 1) return;
                                                    if (e.target.value[0] !== newDeviceType) {
                                                        setNewDeviceId(e.target.value[0] + e.target.value.substring(2));
                                                    } else {
                                                        setNewDeviceId(e.target.value.substring(1));
                                                    }
                                                }}
                                                value={newDeviceType + newDeviceId}
                                                error={deviceIdValid !== ""}
                                                helperText={deviceIdValid}
                                            />
                                        </div>
                                    )}

                                    <div className="confirm-add-sensor-btn-wrapper">
                                        <div className="wrap">
                                            <RectBtn
                                                onClick={async () => {
                                                    if (tabId === "sensor") {
                                                        await validateForm();
                                                    } else if (tabId === "device") {
                                                        await confirmAddDevice();
                                                    }
                                                }}
                                            >
                                                Confirm
                                            </RectBtn>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Slide>
                    </div>
                </div>
            </div>

            <div className="menus">
                <Menu
                    id="delete-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "center", horizontal: "left" }}
                    transformOrigin={{ vertical: "center", horizontal: "right" }}
                >
                    <div className="delete-menu-content">
                        <MenuItem
                            onClick={() => (tabId === "sensor" ? confirmDeleteSensor() : confirmDeleteDevice())}
                            sx={{ justifyContent: "center" }}
                        >
                            <p className="confirm-delete">Confirm Delete</p>
                        </MenuItem>
                        <p className="hint" style={{ fontSize: "1px" }}>
                            This action cannot be restored
                        </p>
                    </div>
                </Menu>
                <Menu
                    id="update-menu"
                    anchorEl={anchorUpdate}
                    open={openUpdate}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "center", horizontal: "left" }}
                    transformOrigin={{ vertical: "center", horizontal: "right" }}
                >
                    <div className="update-menu-content">
                        <div className="fields">
                            <TextField
                                onKeyDown={(e) => e.stopPropagation()}
                                value={updateSensorName}
                                onChange={(e) => setUpdateSensorName(e.target.value)}
                                inputProps={{ maxLength: 30 }}
                                variant="standard"
                                label={"Sensor Name"}
                                error={validUpdateSensorName !== ""}
                                helperText={validUpdateSensorName}
                            />

                            <FormControl variant="standard" sx={{ minWidth: 120 }}>
                                <InputLabel id="select-type">Sensor Type</InputLabel>
                                <Select
                                    labelId="select-type"
                                    id="select-type"
                                    value={updateSensorType}
                                    onChange={(e) => {
                                        setUpdateSensorType(e.target.value);
                                    }}
                                    label="Sensor Type"
                                    sx={{ minWidth: 150 }}
                                >
                                    <MenuItem value={AMMONIA}>Ammonia</MenuItem>
                                    <MenuItem value={PAPER_ROLL}>Paper Roll</MenuItem>
                                    <MenuItem value={HAND_TOWEL}>Hand Towel</MenuItem>
                                    <MenuItem value={SOAP}>Soap</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <div className="update-menu-btn-wrapper">
                            <RectBtn onClick={confirmUpdateSensor}>
                                <p>Confirm</p>
                            </RectBtn>
                        </div>
                    </div>
                </Menu>
            </div>
        </PopUp>
    );
};

export default InspectConfig;
